import type { RasiAccessControlData } from '../accessControlStore';
import type { AuditEvent } from '../investigation/types';
import type { RasiUserEvent } from '../userEventsStore';
import type { RasiAppUserRecord } from '../sessionRegistry';

import { centralFetch } from './client';
import { isCentralDbEnabled } from './config';

export type CentralMonitoringStats = {
  users: number;
  activity: number;
  audit: number;
  userEvents?: number;
  catalog?: { contacts: number; chats: number; messages: number };
  lastUser?: { user_id: string; last_login_at: number };
};

export type AdminAccount = RasiAppUserRecord & {
  catalog?: { contacts: number; chats: number; messages: number };
  updatedAt?: number;
  fullExportUpdatedAt?: number;
  forensicUpdatedAt?: number;
};

export type CatalogContactHit = {
  ownerUserId: string;
  contactId: string;
  name?: string;
  username?: string;
  phone?: string;
  isPremium?: number | boolean;
  updatedAt?: number;
};

export type CatalogChatHit = {
  ownerUserId: string;
  chatId: string;
  title?: string;
  type?: string;
  username?: string;
  lastMessageId?: number;
  lastMessageDate?: number;
  updatedAt?: number;
  messageCount?: number;
};

export type CatalogMessageHit = {
  ownerUserId: string;
  chatId: string;
  messageId: number;
  senderId?: string;
  date: number;
  text?: string;
  isDeleted?: number | boolean;
  updatedAt?: number;
};

export async function fetchCentralUserEvents(userId?: string, limit = 500) {
  if (!isCentralDbEnabled()) return undefined;
  const q = userId ? `&userId=${userId}` : '';
  const res = await centralFetch<{ events: RasiUserEvent[] }>(`/user-events?limit=${limit}${q}`);
  return res?.events;
}

let usersCache: { at: number; users: RasiAppUserRecord[] } | undefined;
const USERS_CACHE_MS = 45_000;

export async function fetchCentralUsers(force = false): Promise<RasiAppUserRecord[] | undefined> {
  if (!isCentralDbEnabled()) return undefined;
  if (!force && usersCache && Date.now() - usersCache.at < USERS_CACHE_MS) {
    return usersCache.users;
  }
  const res = await centralFetch<{ users: RasiAppUserRecord[] }>('/users?limit=2500');
  if (res?.users) {
    usersCache = { at: Date.now(), users: res.users };
    return res.users;
  }
  return undefined;
}

export function invalidateCentralUsersCache() {
  usersCache = undefined;
}

export async function fetchCentralUser(userId: string): Promise<RasiAppUserRecord | undefined> {
  if (!isCentralDbEnabled()) return undefined;
  const res = await centralFetch<{ user: RasiAppUserRecord }>(`/users/${userId}`);
  return res?.user;
}

export async function fetchCentralFullExport(userId: string): Promise<object | undefined> {
  if (!isCentralDbEnabled()) return undefined;
  const res = await centralFetch<{ payload: object }>(`/users/${userId}/full-export`);
  return res?.payload;
}

export async function fetchCentralActivity(limit = 500) {
  if (!isCentralDbEnabled()) return undefined;
  const res = await centralFetch<{ entries: RasiAccessControlData['activityLog'] }>(`/activity?limit=${limit}`);
  return res?.entries;
}

export async function fetchCentralAudit(limit = 500, ownerUserId?: string) {
  if (!isCentralDbEnabled()) return undefined;
  const q = ownerUserId ? `&ownerUserId=${ownerUserId}` : '';
  const res = await centralFetch<{ events: AuditEvent[] }>(`/audit?limit=${limit}${q}`);
  return res?.events;
}

export async function fetchCentralStats(): Promise<CentralMonitoringStats | undefined> {
  if (!isCentralDbEnabled()) return undefined;
  return centralFetch<CentralMonitoringStats>('/stats');
}

/** Admin: all Nusantara accounts across browsers. */
export async function fetchAdminAccounts(limit = 2500) {
  if (!isCentralDbEnabled()) return undefined;
  return centralFetch<{ accounts: AdminAccount[]; catalog: CentralMonitoringStats['catalog'] }>(
    `/admin/accounts?limit=${limit}`,
  );
}

export async function fetchAdminAccount(userId: string) {
  if (!isCentralDbEnabled()) return undefined;
  const res = await centralFetch<{ account: AdminAccount }>(`/admin/accounts/${userId}`);
  return res?.account;
}

export async function fetchAdminLookup(q: string, limit = 50) {
  if (!isCentralDbEnabled()) return undefined;
  return centralFetch<{
    query: string;
    accounts: RasiAppUserRecord[];
    contacts: CatalogContactHit[];
    chats: CatalogChatHit[];
  }>(`/admin/lookup?q=${encodeURIComponent(q)}&limit=${limit}`);
}

export async function fetchAdminChatInfo(chatId: string, ownerUserId?: string) {
  if (!isCentralDbEnabled()) return undefined;
  const q = ownerUserId ? `?ownerUserId=${encodeURIComponent(ownerUserId)}` : '';
  return centralFetch<{ chat: CatalogChatHit | { chatId: string; appearances: CatalogChatHit[] } }>(
    `/admin/chats/${encodeURIComponent(chatId)}${q}`,
  );
}

export async function fetchAdminMessages(params: {
  ownerUserId?: string;
  chatId?: string;
  senderId?: string;
  limit?: number;
  before?: number;
  after?: number;
}) {
  if (!isCentralDbEnabled()) return undefined;
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  const res = await centralFetch<{ messages: CatalogMessageHit[] }>(`/admin/messages?${qs}`);
  return res?.messages;
}

export async function searchAdminMessages(q: string, opts?: { ownerUserId?: string; chatId?: string; limit?: number }) {
  if (!isCentralDbEnabled()) return undefined;
  const qs = new URLSearchParams({ q });
  if (opts?.ownerUserId) qs.set('ownerUserId', opts.ownerUserId);
  if (opts?.chatId) qs.set('chatId', opts.chatId);
  if (opts?.limit) qs.set('limit', String(opts.limit));
  return centralFetch<{ query: string; messages: CatalogMessageHit[] }>(`/admin/search/messages?${qs}`);
}

export async function searchAdminContacts(q: string, opts?: { ownerUserId?: string; limit?: number }) {
  if (!isCentralDbEnabled()) return undefined;
  const qs = new URLSearchParams({ q });
  if (opts?.ownerUserId) qs.set('ownerUserId', opts.ownerUserId);
  if (opts?.limit) qs.set('limit', String(opts.limit));
  return centralFetch<{ query: string; contacts: CatalogContactHit[] }>(`/admin/search/contacts?${qs}`);
}

export async function searchAdminByPhone(phone: string, limit = 50) {
  if (!isCentralDbEnabled()) return undefined;
  return centralFetch<{
    phone: string;
    accounts: RasiAppUserRecord[];
    contacts: CatalogContactHit[];
  }>(`/admin/search/phone?phone=${encodeURIComponent(phone)}&limit=${limit}`);
}
